import { useEffect, useRef, useState } from 'react';
import { CarteDossier, type DossierEnCarte } from './CarteDossier';
// `ECRAN_LARGE` est la constante partagée : c'est le point de rupture qui bascule déjà le
// dossier de la barre du bas au rail de gauche. Un carrousel sur téléphone et une liste sur
// ordinateur sont les deux moitiés de cette même bascule — en écrire une copie ici les
// aurait fait diverger à la première retouche.
import { ECRAN_LARGE, useRequeteMedia } from '../ui/media';

/**
 * La liste des dossiers : empilée sur grand écran, en carrousel sur téléphone.
 *
 * La différence est structurelle — une piste qui défile plutôt qu'une colonne qui
 * s'allonge — donc elle se décide en JS (`useRequeteMedia`), pas en CSS : la feuille de
 * style ne sait pas retirer un `IntersectionObserver` que le composant a déjà posé.
 *
 * Par défaut (requête média indisponible), on sert la variante téléphone : c'est la plus
 * contrainte, donc la plus sûre.
 *
 * Le défilement n'est jamais le seul moyen d'avancer : les puces sont des boutons, pas des
 * indicateurs muets, et restent utilisables sans geste — au clavier ou au clic.
 *
 * Un seul dossier ne montre aucun carrousel, sur aucun écran : il n'y a rien vers quoi
 * glisser, et le décor (puces, piste) n'aurait rien à annoncer.
 */
export function CarrouselDossiers({
  dossiers,
  maintenant,
}: {
  dossiers: DossierEnCarte[];
  maintenant: Date;
}) {
  const large = useRequeteMedia(ECRAN_LARGE);
  const piste = useRef<HTMLDivElement>(null);
  const [actif, setActif] = useState(0);

  const carrousel = dossiers.length > 1 && !large;

  // Suit la diapositive la plus visible pendant le défilement, pour que les puces
  // reflètent le dossier réellement à l'écran — pas seulement la dernière puce cliquée.
  useEffect(() => {
    const noeud = piste.current;
    if (!noeud || !carrousel) return;

    const observateur = new IntersectionObserver(
      (entrees) => {
        const visible = entrees.find((entree) => entree.isIntersecting);
        const index = visible ? Number((visible.target as HTMLElement).dataset.index) : NaN;
        if (!Number.isNaN(index)) setActif(index);
      },
      { root: noeud, threshold: 0.6 },
    );

    for (const diapo of noeud.children) observateur.observe(diapo);
    return () => observateur.disconnect();
  }, [carrousel]);

  if (dossiers.length === 0) return null;

  if (!carrousel) {
    return (
      <>
        {dossiers.map((dossier) => (
          <CarteDossier key={dossier.id} dossier={dossier} maintenant={maintenant} />
        ))}
      </>
    );
  }

  return (
    <div className="carrousel-dossiers">
      <div className="carrousel-piste" ref={piste}>
        {dossiers.map((dossier, index) => (
          <div className="carrousel-diapo" key={dossier.id} data-index={index}>
            <CarteDossier dossier={dossier} maintenant={maintenant} />
          </div>
        ))}
      </div>

      <div className="carrousel-puces" role="tablist" aria-label="Dossiers">
        {dossiers.map((dossier, index) => (
          <button
            key={dossier.id}
            type="button"
            role="tab"
            aria-selected={index === actif}
            aria-label={`Dossier ${index + 1} sur ${dossiers.length}`}
            className={index === actif ? 'carrousel-puce actif' : 'carrousel-puce'}
            onClick={() =>
              piste.current?.children[index]?.scrollIntoView({
                behavior: 'smooth',
                inline: 'center',
                block: 'nearest',
              })
            }
          />
        ))}
      </div>
    </div>
  );
}
